/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { mergeStyleSets } from '@fluentui/merge-styles';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DatePicker, IDatePicker, IDatePickerStyles } from '@fluentui/react/lib/DatePicker';
import { IStackItemStyles, IStackProps } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { TranslationJobStore } from 'src/translation/stores/TranslationJobStore';

export type TranslationCommandBarProps = {
    selectedJobId: string;
    sortedJobs: TranslationJobStore[];
    isStorageAccountsLoading: boolean;
    onEndDateFilter: (endDate: Date) => void;
    onHandleCancelJob: (jobId: string) => void;
    onHandleResubmitJob: (jobId: string) => void;
    onStartDateFilter: (startDate: Date) => void;
};

const messages = defineMessages({
    fromDate: { id: 'translation.translationCommandBar.fromDate', defaultMessage: 'From Date' },
    toDate: {
        id: 'translation.translationCommandBar.toDate',
        defaultMessage: 'To Date'
    },
    resubmitBtn: {
        id: 'translation.translationCommandBar.resubmitBtn',
        defaultMessage: 'Resubmit job'
    },
    cancelBtn: {
        id: 'translation.translationCommandBar.cancelBtn',
        defaultMessage: 'Cancel job'
    },
    cancelBtnTooltip: {
        id: 'translation.translationCommandBar.cancelBtnTooltip',
        defaultMessage: 'Cancel your currently running translation job'
    },
    resubmitBtnTooltip: {
        id: 'translation.translationCommandBar.resubmitBtnTooltip',
        defaultMessage: 'Resubmit your job if it failed. The destination container will be created randomly.'
    }
});

const commandBarStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { paddingLeft: '10px' }
});

const datePickerStyles: Partial<IDatePickerStyles> = { root: { maxWidth: 300, marginTop: 5, minWidth: 150 } };

const addDay = (date: Date) => {
    const newDate = new Date();
    newDate.setDate(date.getDate() + 1);
    return newDate;
};
export const TranslationCommandBar = observer((props: TranslationCommandBarProps) => {
    const { onHandleCancelJob, onStartDateFilter, onEndDateFilter, selectedJobId, sortedJobs } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { translationJobStoreHouse } = rootStore;
    const translationJob = translationJobStoreHouse.findById(selectedJobId);
    const maxDate = addDay(sortedJobs[0]?.createdDateTimeUtc ?? new Date());
    const minDate = sortedJobs[sortedJobs.length - 1]?.createdDateTimeUtc ?? new Date();

    const fromDatePickerRef = React.useRef<IDatePicker>(null);
    const toDatePickerRef = React.useRef<IDatePicker>(null);

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'cancelJob',
            onRender: (item: ICommandBarItemProps) => (
                <LuisTooltip content={localize(messages.cancelBtnTooltip)}>
                    <CommandBarButton
                        id={getId('iconButton')}
                        role={'menuitem'}
                        onClick={() => {
                            onHandleCancelJob(selectedJobId);
                            logDTButtonClick('document translation', 'jobHistory', 'Button', 'cancelJob');
                        }}
                        iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
                        text={localize(messages.cancelBtn)}
                        name={localize(messages.cancelBtn)}
                        data-automation-id="cancelJobButton"
                        onKeyDown={e => {
                            if (e.key === LuisKeyCodes.ArrowRight) {
                                fromDatePickerRef?.current?.focus();
                                e.stopPropagation();
                            }
                        }}
                        disabled={!selectedJobId || translationJob?.status !== 'Running'}
                        styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                    />
                </LuisTooltip>
            )
        }
    ];

    // Removed the resubmit button until we find a better way to do it
    // https://msazure.visualstudio.com/Cognitive%20Services/_workitems/edit/15058796
    // commandBarItems.push({
    //     key: 'resubmitJob',
    //     text: localize(messages.resubmitBtn),
    //     onRender: (item: ICommandBarItemProps) => (
    //         <LuisTooltip content={localize(messages.resubmitBtnTooltip)}>
    //             <CommandBarButton
    //                 id={getId('iconButton')}
    //                 role={'menuitem'}
    //                 onClick={() => onHandleResubmitJob(selectedJobId)}
    //                 iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
    //                 text={localize(messages.resubmitBtn)}
    //                 name={localize(messages.resubmitBtn)}
    //                 data-automation-id="resubmitJobButton"
    //                 disabled={
    //                     !selectedJobId ||
    //                     translationJob?.status !== 'Failed' ||
    //                     translationJob?.documentStoreHouse.items.length === 0 ||
    //                     isStorageAccountsLoading
    //                 }
    //                 styles={getCommandBarButtonStyles(item.renderedInOverflow)}
    //             />
    //         </LuisTooltip>
    //     )
    // });

    const farCommandBarItems: ICommandBarItemProps[] = [
        {
            key: 'fromDateFilter',
            onRender: () => (
                <DatePicker
                    maxDate={maxDate}
                    minDate={minDate}
                    onClick={() => {
                        logDTButtonClick('document translation', 'jobHistory', 'Button', 'filter:fromDate');
                    }}
                    disabled={!sortedJobs.length}
                    componentRef={fromDatePickerRef}
                    onKeyDown={e => {
                        if (e.key === LuisKeyCodes.ArrowRight) {
                            toDatePickerRef?.current?.focus();
                            e.stopPropagation();
                        }
                    }}
                    onSelectDate={onStartDateFilter}
                    ariaLabel={localize(messages.fromDate)}
                    placeholder={localize(messages.fromDate)}
                    styles={mergeStyleSets(datePickerStyles, { root: { marginRight: '5px' } })}
                />
            )
        },
        {
            key: 'toDateFilter',
            onRender: () => (
                <DatePicker
                    minDate={minDate}
                    maxDate={maxDate}
                    onClick={() => {
                        logDTButtonClick('document translation', 'jobHistory', 'Button', 'filter:toDate');
                    }}
                    componentRef={toDatePickerRef}
                    disabled={!sortedJobs.length}
                    styles={datePickerStyles}
                    onKeyDown={e => {
                        if (e.key === LuisKeyCodes.ArrowLeft) {
                            fromDatePickerRef?.current?.focus();
                            e.stopPropagation();
                        }
                    }}
                    onSelectDate={onEndDateFilter}
                    ariaLabel={localize(messages.toDate)}
                    placeholder={localize(messages.toDate)}
                />
            )
        }
    ];

    return <LuisCommandBar items={commandBarItems} styles={commandBarStyles} farItems={farCommandBarItems} />;
});
